import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAvoBalance, getCurrentAvoNetwork, getAvoPrice, getAvoRecentTransactions, getAvoTokenBalances } from '../utils/avoUtils';
import { getFromStorage, saveToStorage, clearSecureStorage } from '../utils/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Box, Typography, Card, CardContent, Button, Grid, AppBar, Toolbar,
  IconButton, CircularProgress, Chip, Stack,
  Badge, Tabs, Tab, Avatar, Divider, Skeleton, Tooltip, useMediaQuery, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, Slide, TextField,
  InputAdornment, Switch, FormControlLabel, BottomNavigation, BottomNavigationAction
} from '@mui/material';
import {
  Send, History, Public, Security, Lock, Delete, ContentCopy, Refresh, ArrowUpward,
  ArrowDownward, CheckCircle, Schedule, Wallet, ExitToApp,
  AccountBalanceWallet, Token, Collections, SwapHoriz, MoreVert,
  QrCode, Visibility, VisibilityOff, Warning, Close, Check, HelpOutline,
  ArrowBack, ArrowForward, AccountBalance, Savings, Home, Payments,
  Gavel, TrendingUp, Hub, VerifiedUser, Group
} from '@mui/icons-material';
import { ThemeProvider, styled } from '@mui/material/styles';
import strictMonochromeTheme from '../utils/strictMonochromeTheme';
import { utils as ethersUtils } from 'ethers';
import { motion, AnimatePresence } from 'framer-motion';
import QRCode from 'react-qr-code';
import Confetti from 'react-dom-confetti';

// Configuración de confetti estricto blanco y negro
const confettiConfig = {
  angle: 90,
  spread: 360,
  startVelocity: 40,
  elementCount: 70,
  dragFriction: 0.12,
  duration: 3000,
  stagger: 3,
  width: "10px",
  height: "10px",
  perspective: "500px",
  colors: ["#FFFFFF"]
};

// Componente de transición para diálogos
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Componente de ítem de transacción
const TransactionItem = ({ tx, network }) => {
  const isReceived = tx.type === 'Received AVO';
  const timestamp = tx.timestamp && !isNaN(new Date(tx.timestamp).getTime())
    ? new Date(tx.timestamp).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unknown date';

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: '#121212',
        borderRadius: '0',
        p: 1.5,
        mb: 1,
        border: '1px solid #424242',
        '&:hover': {
          borderColor: '#FFFFFF'
        }
      }}>
        <Avatar sx={{
          bgcolor: '#121212',
          color: isReceived ? '#FFFFFF' : '#BDBDBD',
          width: 36,
          height: 36,
          mr: 1.5,
          fontSize: '1rem',
          border: '1px solid #424242'
        }}>
          {isReceived ? '↓' : '↑'}
        </Avatar>
        
        <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '0.9rem' }}>
            {tx.type || 'Transaction'}
          </Typography>
          <Typography variant="caption" color="#9E9E9E" display="block" sx={{ fontSize: '0.75rem' }}>
            {timestamp}
          </Typography>
        </Box>
        
        <Box sx={{ textAlign: 'right', minWidth: '80px' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: isReceived ? '#FFFFFF' : '#BDBDBD',
              fontSize: '0.9rem'
            }}
          >
            {isReceived ? '+' : '-'}
            {parseFloat(tx.amount || 0).toFixed(4)}
          </Typography>
          <Typography variant="caption" color="#9E9E9E" display="block" sx={{ fontSize: '0.75rem' }}>
            AVO
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

// Componente de diálogo para eliminar cuenta
const DeleteProtectionDialog = ({ open, onClose, onConfirm }) => {
  const [step, setStep] = useState(1);
  const [confirmationText, setConfirmationText] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);

  const requiredText = "DELETE MY WALLET";

  const handleReset = () => {
    setStep(1);
    setConfirmationText('');
    setPassword('');
    setIsConfirmed(false);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleConfirm = () => {
    setIsConfirmed(true);
    setTimeout(() => {
      onConfirm(password);
      handleClose();
    }, 1500);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#121212',
          borderRadius: '0',
          border: '1px solid #424242',
          boxShadow: '0 0 15px rgba(255,255,255,0.1)',
          mx: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        bgcolor: '#121212', 
        color: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        fontSize: '1.1rem',
        borderBottom: '1px solid #424242'
      }}>
        <Warning sx={{ mr: 1, color: '#9E9E9E', fontSize: '1.2rem' }} />
        <Typography variant="h6" fontWeight={500} sx={{ fontSize: '1.1rem' }}>
          {step === 1 ? 'CONFIRM DELETION' : 'FINAL VERIFICATION'}
        </Typography>
        <IconButton
          edge="end"
          color="inherit"
          onClick={handleClose}
          sx={{ 
            ml: 'auto', 
            color: '#FFFFFF',
            '&:hover': { backgroundColor: 'transparent' },
            padding: '8px'
          }}
        >
          <Close sx={{ fontSize: '1.2rem' }} />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ py: 2, bgcolor: '#121212', color: '#FFFFFF' }}>
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <DialogContentText color="#FFFFFF" sx={{ mb: 2, fontSize: '0.85rem' }}>
                THIS ACTION IS IRREVERSIBLE. BY DELETING YOUR WALLET:
              </DialogContentText>
              
              <Box sx={{ 
                bgcolor: '#121212',
                borderLeft: '3px solid #424242',
                p: 1.5,
                mb: 2,
                border: '1px solid #424242'
              }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="#FFFFFF" sx={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                    <Close sx={{ fontSize: 14, mr: 1, mt: '2px', color: '#9E9E9E' }} />
                    ALL YOUR FUNDS AND DATA WILL BE PERMANENTLY INACCESSIBLE
                  </Typography>
                  <Typography variant="body2" color="#FFFFFF" sx={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                    <Close sx={{ fontSize: 14, mr: 1, mt: '2px', color: '#9E9E9E' }} />
                    YOU WILL NOT BE ABLE TO RECOVER THIS WALLET WITH YOUR SEED PHRASE
                  </Typography>
                  <Typography variant="body2" color="#FFFFFF" sx={{ display: 'flex', alignItems: 'flex-start', fontSize: '0.85rem' }}>
                    <Close sx={{ fontSize: 14, mr: 1, mt: '2px', color: '#9E9E9E' }} />
                    ANY ASSOCIATED ASSETS WILL BE LOST FOREVER
                  </Typography>
                </Stack>
              </Box>
              
              <DialogContentText color="#FFFFFF" sx={{ mb: 1.5, fontSize: '0.85rem' }}>
                TO PROCEED, TYPE <strong>{requiredText}</strong> BELOW:
              </DialogContentText>
              
              <TextField
                fullWidth
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={requiredText}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#424242' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#FFFFFF' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.9rem',
                    padding: '10px 12px',
                    color: '#FFFFFF'
                  }
                }}
              />
            </motion.div>
          )}
          
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              <DialogContentText color="#FFFFFF" sx={{ mb: 2, fontSize: '0.85rem' }}>
                AS A FINAL SECURITY MEASURE, ENTER YOUR PASSWORD TO CONFIRM DELETION:
              </DialogContentText>
              
              <TextField
                fullWidth
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="YOUR PASSWORD"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ 
                          color: '#FFFFFF',
                          padding: '8px',
                          '&:hover': { backgroundColor: 'transparent' }
                        }}
                      >
                        {showPassword ? 
                          <VisibilityOff sx={{ fontSize: '18px' }} /> : 
                          <Visibility sx={{ fontSize: '18px' }} />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  mb: 2,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '0',
                    backgroundColor: '#121212',
                    '& fieldset': { borderColor: '#424242' },
                    '&:hover fieldset': { borderColor: '#FFFFFF' },
                    '&.Mui-focused fieldset': { borderColor: '#FFFFFF' },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '0.9rem',
                    padding: '10px 12px',
                    color: '#FFFFFF'
                  }
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 2, 
        py: 1.5,
        borderTop: '1px solid #424242',
        bgcolor: '#121212'
      }}>
        {step === 1 ? (
          <>
            <Button 
              onClick={handleClose}
              sx={{ 
                color: '#FFFFFF',
                fontSize: '0.85rem',
                minWidth: '80px',
                '&:hover': { backgroundColor: 'transparent' }
              }}
            >
              CANCEL
            </Button>
            <Button
              onClick={() => setStep(2)}
              disabled={confirmationText !== requiredText}
              sx={{ 
                backgroundColor: '#FFFFFF',
                color: '#000000',
                fontSize: '0.85rem',
                minWidth: '100px',
                '&:hover': { backgroundColor: '#FFFFFF' },
                '&.Mui-disabled': { backgroundColor: '#121212', color: '#424242', borderColor: '#424242' },
                borderRadius: '0'
              }}
              endIcon={<ArrowForward sx={{ fontSize: '18px' }} />}
            >
              CONTINUE
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={() => setStep(1)}
              sx={{ 
                color: '#FFFFFF',
                fontSize: '0.85rem',
                minWidth: '80px',
                '&:hover': { backgroundColor: 'transparent' }
              }}
              startIcon={<ArrowBack sx={{ fontSize: '18px' }} />}
            >
              BACK
            </Button>
            <Box sx={{ position: 'relative' }}>
              <Button
                onClick={handleConfirm}
                disabled={!password || isConfirmed}
                sx={{ 
                  backgroundColor: '#FFFFFF',
                  color: '#000000',
                  fontSize: '0.85rem',
                  minWidth: '100px',
                  '&:hover': { backgroundColor: '#FFFFFF' },
                  '&.Mui-disabled': { backgroundColor: '#121212', color: '#424242', borderColor: '#424242' },
                  borderRadius: '0'
                }}
                endIcon={isConfirmed ? <Check sx={{ fontSize: '18px' }} /> : <Delete sx={{ fontSize: '18px' }} />}
              >
                {isConfirmed ? 'CONFIRMED' : 'DELETE'}
              </Button>
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <Confetti active={isConfirmed} config={confettiConfig} />
              </Box>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

/**
 * Componente principal del dashboard con estilo monocromático radical
 */
function Dashboard({ wallet, setWallet, setIsSessionActive }) {
  const [balance, setBalance] = useState('0');
  const [avoPrice, setAvoPrice] = useState(0);
  const [network, setNetwork] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [networkStatus, setNetworkStatus] = useState('connecting');
  const [priceChange24h, setPriceChange24h] = useState(0);
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [showFullAddress, setShowFullAddress] = useState(false);
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  // Validar sesión y cargar datos
  useEffect(() => {
    const validateSession = async () => {
      setIsLoading(true);
      try {
        const sessionActive = await getFromStorage('sessionActive');
        const encryptedWallet = await getFromStorage('cryptoWallet');

        if (sessionActive !== 'true' || !encryptedWallet) {
          setWallet(null);
          setIsSessionActive(false);
          toast.info('SESSION NOT ACTIVE. PLEASE LOG IN.', {
            position: 'top-center',
            style: { 
              background: '#121212',
              color: '#FFFFFF',
              border: '1px solid #424242',
              borderRadius: '0'
            },
            onClose: () => navigate('/login')
          });
          return;
        }

        if (!wallet || !wallet.address || !ethersUtils.isAddress(wallet.address)) {
          toast.error('INVALID WALLET DATA. PLEASE LOG IN AGAIN.', {
            position: 'top-center',
            style: { 
              background: '#121212',
              color: '#FFFFFF',
              border: '1px solid #424242',
              borderRadius: '0'
            },
            onClose: () => navigate('/login')
          });
          return;
        }

        const timeout = setTimeout(async () => {
          await saveToStorage('sessionActive', 'false');
          setWallet(null);
          setIsSessionActive(false);
          toast.info('SESSION EXPIRED DUE TO INACTIVITY', {
            position: 'top-center',
            style: { 
              background: '#121212',
              color: '#FFFFFF',
              border: '1px solid #424242',
              borderRadius: '0'
            },
            onClose: () => navigate('/login')
          });
        }, 30 * 60 * 1000);

        await loadWalletData();

        return () => clearTimeout(timeout);
      } catch (err) {
        console.error('SESSION VALIDATION ERROR:', err);
        toast.error('ERROR VALIDATING SESSION. PLEASE LOG IN.', {
          position: 'top-center',
          style: { 
            background: '#121212',
            color: '#FFFFFF',
            border: '1px solid #424242',
            borderRadius: '0'
          },
          onClose: () => navigate('/login')
        });
      } finally {
        setIsLoading(false);
      }
    };

    validateSession();
  }, [wallet, setWallet, setIsSessionActive, navigate]);

  // Cargar datos de la wallet
  const loadWalletData = async () => {
    if (!wallet || !wallet.address || !ethersUtils.isAddress(wallet.address)) {
      setNetworkStatus('error');
      toast.error('NO VALID WALLET CONNECTED', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
      return;
    }

    setIsLoading(true);
    try {
      const [currentBalance, networkName, avoPriceData, recentTransactions, tokenBalances] = await Promise.all([
        getAvoBalance(wallet.address),
        getCurrentAvoNetwork(),
        getAvoPrice(),
        getAvoRecentTransactions(wallet.address),
        getAvoTokenBalances(wallet.address)
      ]);

      setBalance(currentBalance || '0');
      setNetwork(networkName?.name || 'AVO Network');
      setAvoPrice(parseFloat(avoPriceData?.currentPrice) || 0);
      setPriceChange24h(parseFloat(avoPriceData?.priceChange24h) || 0);
      setPortfolioValue((parseFloat(currentBalance || 0) * parseFloat(avoPriceData?.currentPrice || 0)) || 0);
      
      const validTransactions = Array.isArray(recentTransactions)
        ? recentTransactions.filter(tx => tx && tx.hash && tx.amount && tx.timestamp)
        : [];
      setTransactions(validTransactions.slice(0, 5));
      
      const validTokens = Array.isArray(tokenBalances)
        ? tokenBalances.filter(token => parseFloat(token.balance) > 0)
        : [];
      setTokens(validTokens);
      
      setNetworkStatus('connected');
    } catch (err) {
      console.error('ERROR LOADING WALLET DATA:', err);
      setNetworkStatus('error');
      toast.error('ERROR LOADING DATA. PLEASE TRY AGAIN.', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Actualizaciones periódicas del precio
  useEffect(() => {
    const priceInterval = setInterval(() => {
      getAvoPrice().then(priceData => {
        setAvoPrice(parseFloat(priceData?.currentPrice) || 0);
        setPriceChange24h(parseFloat(priceData?.priceChange24h) || 0);
      });
    }, 60000);
    
    return () => clearInterval(priceInterval);
  }, []);

  // Manejar refresco de datos
  const handleRefresh = async () => {
    if (!wallet?.address) {
      toast.error('NO WALLET CONNECTED', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
      return;
    }

    setIsLoading(true);
    try {
      await loadWalletData();
      toast.success('DATA REFRESHED', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
    } catch (err) {
      console.error('REFRESH ERROR:', err);
      toast.error('ERROR REFRESHING DATA', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Copiar dirección al portapapeles
  const handleCopyAddress = () => {
    if (!wallet?.address) {
      toast.error('NO ADDRESS TO COPY', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
      return;
    }
    navigator.clipboard.writeText(wallet.address);
    toast.success('ADDRESS COPIED', {
      position: 'top-center',
      style: { 
        background: '#121212',
        color: '#FFFFFF',
        border: '1px solid #424242',
        borderRadius: '0'
      }
    });
  };

  // Eliminar cuenta
  const handleDeleteAccount = async (password) => {
    try {
      await clearSecureStorage();
      
      // Limpiar todos los datos de sesión
      localStorage.removeItem('privateKey');
      localStorage.removeItem('mnemonic');
      localStorage.removeItem('address');
      
      setWallet(null);
      setIsSessionActive(false);
      await saveToStorage('sessionActive', 'false');
      toast.success('WALLET PERMANENTLY DELETED', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        },
        onClose: () => navigate('/')
      });
    } catch (err) {
      console.error('DELETE ERROR:', err);
      toast.error('ERROR DELETING WALLET. PLEASE VERIFY YOUR PASSWORD.', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
    }
  };

  // Cerrar sesión
  const handleLogout = async () => {
    try {
      await saveToStorage('sessionActive', 'false');
      
      // Limpiar datos de sesión de localStorage
      localStorage.removeItem('privateKey');
      localStorage.removeItem('mnemonic');
      localStorage.removeItem('address');
      
      setWallet(null);
      setIsSessionActive(false);
      toast.success('LOGGED OUT SUCCESSFULLY', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        },
        onClose: () => navigate('/login')
      });
    } catch (err) {
      console.error('ERROR LOGGING OUT:', err);
      toast.error('ERROR LOGGING OUT.', {
        position: 'top-center',
        style: { 
          background: '#121212',
          color: '#FFFFFF',
          border: '1px solid #424242',
          borderRadius: '0'
        }
      });
    }
  };

  // Validar si no hay wallet
  if (!wallet?.address || !ethersUtils.isAddress(wallet.address)) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ 
          minHeight: '100vh', 
          bgcolor: '#000000', 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          px: 3,
          py: 4
        }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 2, color: '#FFFFFF', textAlign: 'center' }}>
              WALLET NOT DETECTED
            </Typography>
            <Typography variant="body1" color="#9E9E9E" sx={{ mb: 3, textAlign: 'center' }}>
              CONNECT OR CREATE A WALLET TO GET STARTED
            </Typography>
            <Button
              component={Link}
              to="/create-wallet"
              startIcon={<Wallet />}
              sx={{ 
                mb: 2, 
                width: '100%',
                borderRadius: '0',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                color: '#FFFFFF',
                border: '1px solid #FFFFFF',
                '&:hover': { 
                  backgroundColor: '#FFFFFF', 
                  color: '#000000',
                  border: '1px solid #FFFFFF'
                }
              }}
            >
              CREATE WALLET
            </Button>
            <Button
              variant="outlined"
              component={Link}
              to="/login"
              startIcon={<ExitToApp />}
              sx={{ 
                borderRadius: '0',
                py: 1.5,
                fontWeight: 500,
                textTransform: 'uppercase',
                letterSpacing: '2px',
                color: '#FFFFFF',
                border: '1px solid #424242',
                '&:hover': { borderColor: '#FFFFFF', backgroundColor: '#121212' },
                width: '100%'
              }}
            >
              LOG IN
            </Button>
          </motion.div>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={strictMonochromeTheme}>
      <Box sx={{ 
        minHeight: '100vh', 
        bgcolor: '#000000',
        display: 'flex',
        flexDirection: 'column',
        pb: isMobile ? '56px' : 0
      }}>
        {/* Toast notifications */}
        <ToastContainer 
          limit={3} 
          newestOnTop 
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          draggable
          style={{ zIndex: 1400 }}
          toastStyle={{
            backgroundColor: '#121212',
            color: '#FFFFFF',
            borderRadius: '0',
            border: '1px solid #424242',
            boxShadow: '0 0 15px rgba(255,255,255,0.1)',
          }}
        />
        
        {/* Diálogo para eliminar cuenta */}
        <DeleteProtectionDialog 
          open={deleteDialogOpen} 
          onClose={() => setDeleteDialogOpen(false)} 
          onConfirm={handleDeleteAccount} 
        />
        
        {/* Diálogo para QR Code */}
        <Dialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: '#121212',
              borderRadius: '0',
              p: 2,
              textAlign: 'center',
              border: '1px solid #424242',
              mx: isMobile ? 2 : 'auto'
            }
          }}
        >
          <DialogTitle sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '1.2rem', py: 1, borderBottom: '1px solid #424242' }}>
            YOUR WALLET ADDRESS
          </DialogTitle>
          <QRCode 
            value={wallet.address} 
            size={200} 
            level="H" 
            style={{ 
              width: '100%', 
              height: 'auto',
              maxWidth: 200,
              margin: '0 auto',
              padding: '12px',
              backgroundColor: '#FFFFFF',
              borderRadius: '0'
            }} 
          />
          <Typography variant="body2" color="#9E9E9E" sx={{ mt: 1.5, wordBreak: 'break-all', fontSize: '0.8rem' }}>
            {wallet.address}
          </Typography>
          <Button 
            onClick={handleCopyAddress}
            startIcon={<ContentCopy sx={{ fontSize: '18px' }} />}
            sx={{ 
              mt: 1.5, 
              color: '#FFFFFF',
              fontSize: '0.85rem',
              '&:hover': { backgroundColor: 'transparent' }
            }}
          >
            COPY ADDRESS
          </Button>
        </Dialog>
        
        {/* Barra superior */}
        <AppBar 
          position="sticky" 
          elevation={0} 
          sx={{ 
            bgcolor: '#000000',
            borderBottom: '1px solid #FFFFFF',
            boxShadow: 'none'
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: 1.5, px: 3 }}>
            <Typography variant="h6" sx={{ 
              fontWeight: 700,
              color: '#FFFFFF',
              fontSize: '1.2rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              letterSpacing: '2px',
              textTransform: 'uppercase'
            }}>
              AVO_STATION
            </Typography>
            
            <Stack direction="row" spacing={2} alignItems="center">
              <Tooltip title={network}>
                <Chip
                  label={networkStatus === 'connected' ? network.split(' ')[0] : 'DISCONNECTED'}
                  sx={{ 
                    fontWeight: 600, 
                    bgcolor: '#000000',
                    color: networkStatus === 'connected' ? '#FFFFFF' : '#9E9E9E',
                    borderRadius: '0',
                    fontSize: '0.75rem',
                    height: '30px',
                    border: '1px solid #FFFFFF',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                      color: '#000000'
                    }
                  }}
                />
              </Tooltip>
              
              <Button
                startIcon={<Refresh />}
                onClick={handleRefresh}
                disabled={isLoading}
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000'
                  },
                  '&.Mui-disabled': {
                    borderColor: '#424242',
                    color: '#424242'
                  }
                }}
              >
                REFRESH
              </Button>
              
              <Button
                startIcon={<History />}
                component={Link}
                to="/history"
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000'
                  }
                }}
              >
                HISTORY
              </Button>
              
              <Button
                startIcon={<Security />}
                component={Link}
                to="/setup2fa"
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000'
                  }
                }}
              >
                SECURITY
              </Button>
              
              <Button
                startIcon={<Public />}
                component={Link}
                to="/network"
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000'
                  }
                }}
              >
                NETWORK
              </Button>

              <Button
                startIcon={<Gavel />}
                component={Link}
                to="/governance"
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000'
                  }
                }}
              >
                GOVERNANCE
              </Button>
              
              <Button
                startIcon={<Delete />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  color: '#9E9E9E',
                  border: '1px solid #424242',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#9E9E9E',
                    color: '#000000',
                    borderColor: '#9E9E9E'
                  }
                }}
              >
                DELETE
              </Button>
              
              <Button
                startIcon={<ExitToApp />}
                onClick={handleLogout}
                sx={{
                  color: '#FFFFFF',
                  border: '1px solid #FFFFFF',
                  borderRadius: 0,
                  px: 2,
                  py: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: '#FFFFFF',
                    color: '#000000'
                  }
                }}
              >
                LOGOUT
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>
        
        {/* Contenido principal */}
        <Box sx={{ 
          flexGrow: 1,
          px: 2,
          py: 2,
          overflowY: 'auto',
          '-webkit-overflow-scrolling': 'touch'
        }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rounded" height={150} sx={{ borderRadius: '0', bgcolor: '#121212' }} />
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: '0', bgcolor: '#121212' }} />
              <Skeleton variant="rounded" height={100} sx={{ borderRadius: '0', bgcolor: '#121212' }} />
              <Skeleton variant="rounded" height={250} sx={{ borderRadius: '0', bgcolor: '#121212' }} />
            </Box>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              {/* Sección de dirección y balance */}
              <Card sx={{ mb: 2, p: 2, border: '1px solid #424242' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="#9E9E9E" sx={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                    YOUR ADDRESS
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="COPY ADDRESS">
                      <IconButton 
                        onClick={handleCopyAddress} 
                        sx={{ 
                          color: '#9E9E9E',
                          padding: '6px',
                          border: '1px solid #424242',
                          borderRadius: '0',
                          '&:hover': { 
                            backgroundColor: '#FFFFFF',
                            color: '#000000',
                            borderColor: '#FFFFFF'
                          }
                        }}
                      >
                        <ContentCopy sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="SHOW QR CODE">
                      <IconButton 
                        onClick={() => setQrOpen(true)} 
                        sx={{ 
                          color: '#9E9E9E',
                          padding: '6px',
                          border: '1px solid #424242',
                          borderRadius: '0',
                          '&:hover': { 
                            backgroundColor: '#FFFFFF',
                            color: '#000000',
                            borderColor: '#FFFFFF'
                          }
                        }}
                      >
                        <QrCode sx={{ fontSize: '18px' }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
                
                <Box 
                  onClick={() => setShowFullAddress(!showFullAddress)}
                  sx={{
                    p: 1.5,
                    mb: 1,
                    bgcolor: '#121212',
                    borderRadius: '0',
                    border: '1px solid #424242',
                    cursor: 'pointer',
                    '&:hover': {
                      borderColor: '#FFFFFF'
                    }
                  }}
                >
                  <Typography variant="body2" sx={{ 
                    fontFamily: '"IBM Plex Mono", monospace',
                    wordBreak: 'break-all',
                    color: '#FFFFFF',
                    fontSize: '0.8rem'
                  }}>
                    {showFullAddress ? wallet.address : `${wallet.address.substring(0, 8)}...${wallet.address.substring(wallet.address.length - 4)}`}
                  </Typography>
                </Box>
                
                <Typography variant="subtitle2" color="#9E9E9E" mb={0.5} sx={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                  TOTAL BALANCE
                </Typography>
                <Typography variant="h4" sx={{ 
                  fontWeight: 600,
                  mb: 0.5,
                  color: '#FFFFFF',
                  fontSize: '1.8rem',
                  letterSpacing: '1px'
                }}>
                  {parseFloat(balance || 0).toFixed(4)} AVO
                </Typography>
                
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, color: '#9E9E9E', fontSize: '1.2rem', letterSpacing: '1px' }}>
                  ${(parseFloat(balance || 0) * parseFloat(avoPrice || 0)).toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </Typography>
                
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Button
                      startIcon={<Send sx={{ fontSize: '18px' }} />}
                      fullWidth
                      component={Link}
                      to="/send"
                      sx={{ 
                        py: 1, 
                        fontSize: '0.9rem',
                        borderRadius: '0',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: '#FFFFFF',
                        border: '1px solid #FFFFFF',
                        '&:hover': { 
                          backgroundColor: '#FFFFFF', 
                          color: '#000000',
                          border: '1px solid #FFFFFF'
                        }
                      }}
                    >
                      SEND
                    </Button>
                  </Grid>
                </Grid>
                
                {/* AVO Features - Segunda fila */}
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      startIcon={<Gavel sx={{ fontSize: '18px' }} />}
                      fullWidth
                      component={Link}
                      to="/governance"
                      sx={{
                        borderRadius: '0',
                        py: 1,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: '#FFFFFF',
                        border: '1px solid #424242',
                        fontSize: '0.8rem',
                        '&:hover': { borderColor: '#FFFFFF', backgroundColor: '#121212' }
                      }}
                    >
                      GOVERNANCE
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      startIcon={<Hub sx={{ fontSize: '18px' }} />}
                      fullWidth
                      component={Link}
                      to="/bootstrap-node"
                      sx={{
                        borderRadius: '0',
                        py: 1,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: '#FFFFFF',
                        border: '1px solid #424242',
                        fontSize: '0.8rem',
                        '&:hover': { borderColor: '#FFFFFF', backgroundColor: '#121212' }
                      }}
                    >
                      BOOTSTRAP NODE
                    </Button>
                  </Grid>
                </Grid>
                
                <Grid container spacing={1} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      startIcon={<VerifiedUser sx={{ fontSize: '18px' }} />}
                      fullWidth
                      component={Link}
                      to="/validators"
                      sx={{
                        borderRadius: '0',
                        py: 1,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: '#FFFFFF',
                        border: '1px solid #424242',
                        fontSize: '0.8rem',
                        '&:hover': { borderColor: '#FFFFFF', backgroundColor: '#121212' }
                      }}
                    >
                      VALIDATORS
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      startIcon={<Group sx={{ fontSize: '18px' }} />}
                      fullWidth
                      component={Link}
                      to="/delegators"
                      sx={{
                        borderRadius: '0',
                        py: 1,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        color: '#FFFFFF',
                        border: '1px solid #424242',
                        fontSize: '0.8rem',
                        '&:hover': { borderColor: '#FFFFFF', backgroundColor: '#121212' }
                      }}
                    >
                      DELEGATORS
                    </Button>
                  </Grid>
                </Grid>
              </Card>
              
              {/* Tarjeta de Portfolio Value */}
              <Card sx={{ mb: 2, p: 2, border: '1px solid #424242' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <AccountBalanceWallet sx={{ color: '#FFFFFF', fontSize: '18px' }} />
                  <Typography variant="subtitle2" color="#9E9E9E" sx={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                    PORTFOLIO VALUE
                  </Typography>
                </Stack>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5, color: '#FFFFFF', fontSize: '1.4rem', letterSpacing: '1px' }}>
                  ${portfolioValue.toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {priceChange24h >= 0 ? (
                    <ArrowUpward sx={{ fontSize: 14, color: '#FFFFFF' }} />
                  ) : (
                    <ArrowDownward sx={{ fontSize: 14, color: '#9E9E9E' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: priceChange24h >= 0 ? '#FFFFFF' : '#9E9E9E',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      letterSpacing: '1px'
                    }}
                  >
                    {Math.abs(priceChange24h).toFixed(2)}% (24H)
                  </Typography>
                </Stack>
              </Card>
              
              {/* Tarjeta de Precio */}
              <Card sx={{ mb: 2, p: 2, border: '1px solid #424242' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <AccountBalance sx={{ color: '#FFFFFF', fontSize: '18px' }} />
                  <Typography variant="subtitle2" color="#9E9E9E" sx={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                    AVO PRICE
                  </Typography>
                </Stack>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5, color: '#FFFFFF', fontSize: '1.4rem', letterSpacing: '1px' }}>
                  ${avoPrice.toLocaleString('en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {priceChange24h >= 0 ? (
                    <ArrowUpward sx={{ fontSize: 14, color: '#FFFFFF' }} />
                  ) : (
                    <ArrowDownward sx={{ fontSize: 14, color: '#9E9E9E' }} />
                  )}
                  <Typography
                    variant="body2"
                    sx={{ 
                      color: priceChange24h >= 0 ? '#FFFFFF' : '#9E9E9E',
                      fontWeight: 500,
                      fontSize: '0.8rem',
                      letterSpacing: '1px'
                    }}
                  >
                    {Math.abs(priceChange24h).toFixed(2)}% (24H)
                  </Typography>
                </Stack>
              </Card>
              
              {/* Pestañas de navegación */}
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons={false}
                sx={{ 
                  mb: 2,
                  '& .MuiTabs-indicator': {
                    height: 2,
                    backgroundColor: '#FFFFFF'
                  }
                }}
              >
                <Tab 
                  label="ASSETS" 
                  icon={<AccountBalanceWallet sx={{ fontSize: '18px' }} />} 
                  iconPosition="start" 
                  sx={{ minWidth: 'auto' }}
                />
                <Tab 
                  label="ACTIVITY" 
                  icon={<History sx={{ fontSize: '18px' }} />} 
                  iconPosition="start" 
                  sx={{ minWidth: 'auto' }}
                />
                <Tab 
                  label="NFTS" 
                  icon={<Collections sx={{ fontSize: '18px' }} />} 
                  iconPosition="start" 
                  disabled 
                  sx={{ minWidth: 'auto' }}
                />
              </Tabs>
              
              {/* Contenido de las pestañas */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {activeTab === 0 && (
                    <Card sx={{ mb: 2, p: 2, border: '1px solid #424242' }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: '#FFFFFF', fontSize: '1.1rem', letterSpacing: '1px' }}>
                        YOUR ASSETS
                      </Typography>
                      
                      {/* Token nativo */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          p: 1.5,
                          mb: 1,
                          borderRadius: '0',
                          bgcolor: '#121212',
                          border: '1px solid #424242',
                          '&:hover': {
                            borderColor: '#FFFFFF'
                          }
                        }}
                      >
                        <Avatar sx={{ 
                          bgcolor: '#121212', 
                          color: '#FFFFFF',
                          mr: 1.5,
                          width: 36,
                          height: 36,
                          fontSize: '0.9rem',
                          border: '1px solid #424242'
                        }}>
                          A
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '0.9rem' }}>
                            AVO
                          </Typography>
                          <Typography variant="caption" color="#9E9E9E" sx={{ fontSize: '0.75rem' }}>
                            {network}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '0.9rem' }}>
                            {parseFloat(balance || 0).toFixed(4)}
                          </Typography>
                          <Typography variant="caption" color="#9E9E9E" sx={{ fontSize: '0.75rem' }}>
                            ${(parseFloat(balance || 0) * parseFloat(avoPrice || 0)).toLocaleString('en-US', { 
                              minimumFractionDigits: 2, 
                              maximumFractionDigits: 2 
                            })}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Tokens */}
                      {tokens.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="subtitle2" color="#9E9E9E" mb={1} sx={{ fontSize: '0.8rem', letterSpacing: '1px' }}>
                            TOKENS ({tokens.length})
                          </Typography>
                          {tokens.map((token, index) => (
                            <Box 
                              key={index}
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                p: 1.5,
                                mb: 1,
                                borderRadius: '0',
                                bgcolor: '#121212',
                                border: '1px solid #424242',
                                '&:hover': {
                                  borderColor: '#FFFFFF'
                                }
                              }}
                            >
                              <Avatar 
                                src={token.logo} 
                                sx={{ 
                                  mr: 1.5, 
                                  width: 36,
                                  height: 36,
                                  bgcolor: '#121212', 
                                  color: '#FFFFFF',
                                  fontSize: '0.9rem',
                                  border: '1px solid #424242'
                                }}
                              >
                                {token.symbol?.charAt(0)}
                              </Avatar>
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '0.9rem' }}>
                                  {token.name}
                                </Typography>
                                <Typography variant="caption" color="#9E9E9E" sx={{ fontSize: '0.75rem' }}>
                                  {token.symbol}
                                </Typography>
                              </Box>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '0.9rem' }}>
                                  {parseFloat(token.balance || 0).toFixed(4)}
                                </Typography>
                                <Typography variant="caption" color="#9E9E9E" sx={{ fontSize: '0.75rem' }}>
                                  ${(parseFloat(token.balance || 0) * parseFloat(token.price || 0)).toLocaleString('en-US', { 
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2 
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      )}
                      
                      {tokens.length === 0 && (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 2,
                          textAlign: 'center'
                        }}>
                          <Token sx={{ fontSize: 36, color: '#9E9E9E', mb: 1 }} />
                          <Typography variant="body2" sx={{ mb: 1, color: '#FFFFFF', fontSize: '0.9rem' }}>
                            YOU DON'T HAVE ANY TOKENS YET
                          </Typography>
                        </Box>
                      )}
                    </Card>
                  )}
                  
                  {activeTab === 1 && (
                    <Card sx={{ mb: 2, p: 2, border: '1px solid #424242' }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#FFFFFF', fontSize: '1.1rem', letterSpacing: '1px' }}>
                          RECENT ACTIVITY
                        </Typography>
                        <Button
                          component={Link}
                          to="/history"
                          variant="text"
                          sx={{ 
                            fontWeight: 500, 
                            color: '#FFFFFF',
                            fontSize: '0.85rem',
                            '&:hover': { backgroundColor: 'transparent' }
                          }}
                        >
                          VIEW ALL
                        </Button>
                      </Stack>
                      
                      {transactions.length > 0 ? (
                        <Stack spacing={1}>
                          {transactions.map((tx, index) => (
                            <TransactionItem 
                              key={index} 
                              tx={tx} 
                              network={network}
                            />
                          ))}
                        </Stack>
                      ) : (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 2,
                          textAlign: 'center'
                        }}>
                          <History sx={{ fontSize: 36, color: '#9E9E9E', mb: 1 }} />
                          <Typography variant="body2" sx={{ mb: 1, color: '#FFFFFF', fontSize: '0.9rem' }}>
                            NO RECENT ACTIVITY
                          </Typography>
                          <Button 
                            variant="text" 
                            component={Link} 
                            to="/send"
                            sx={{ 
                              fontWeight: 500, 
                              color: '#FFFFFF',
                              fontSize: '0.85rem',
                              '&:hover': { backgroundColor: 'transparent' }
                            }}
                          >
                            MAKE YOUR FIRST TRANSACTION
                          </Button>
                        </Box>
                      )}
                    </Card>
                  )}
                  
                  {activeTab === 2 && (
                    <Card sx={{ mb: 2, p: 2, border: '1px solid #424242' }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, mb: 1, color: '#FFFFFF', fontSize: '1.1rem', letterSpacing: '1px' }}>
                        YOUR NFTS
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        py: 2,
                        textAlign: 'center'
                      }}>
                        <Collections sx={{ fontSize: 36, color: '#9E9E9E', mb: 1 }} />
                        <Typography variant="body2" sx={{ mb: 1, color: '#FFFFFF', fontSize: '0.9rem' }}>
                          YOU DON'T HAVE ANY NFTS YET
                        </Typography>
                        <Button 
                          variant="text" 
                          component={Link} 
                          to="/nfts"
                          disabled
                          sx={{ 
                            fontWeight: 500, 
                            color: '#424242',
                            fontSize: '0.85rem',
                            '&:hover': { backgroundColor: 'transparent' }
                          }}
                        >
                          EXPLORE NFTS
                        </Button>
                      </Box>
                    </Card>
                  )}
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}
        </Box>
        
        {/* Navegación inferior para móviles */}
        {isMobile && (
          <BottomNavigation
            value={activeTab}
            onChange={(event, newValue) => setActiveTab(newValue)}
            showLabels
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: '#121212',
              borderTop: '1px solid #424242',
              zIndex: 1000,
              '& .MuiBottomNavigationAction-root': {
                minWidth: 'auto',
                px: 0.5,
                color: '#9E9E9E',
                '&.Mui-selected': {
                  color: '#FFFFFF'
                }
              }
            }}
          >
            <BottomNavigationAction 
              label="ASSETS" 
              icon={<AccountBalanceWallet sx={{ fontSize: '24px' }} />} 
            />
            <BottomNavigationAction 
              label="ACTIVITY" 
              icon={<History sx={{ fontSize: '24px' }} />} 
            />
            <BottomNavigationAction 
              label="NFTS" 
              icon={<Collections sx={{ fontSize: '24px' }} />}
              disabled
            />
            <BottomNavigationAction 
              label="MORE" 
              icon={<MoreVert sx={{ fontSize: '24px' }} />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            />
          </BottomNavigation>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default Dashboard;